import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { exposeStrapiError } from 'utils'

import useI18n from 'hooks/useI18n'
import {
  ApproveMyPendingAccess,
  ReadMyPendingAccessCompanies,
  ReadMyPendingAccess,
  ReadMyPendingIdentityReview,
  RejectMyPendingAccess,
} from 'services/approval'

const PAGE_SIZE = 10

export const getApprovalErrorKey = status => {
  if (status === 401) return 'user_approvals_auth_required'
  if (status === 403) return 'user_approvals_not_authorized'
  if (status === 0) return 'user_approvals_network_error'
  return 'user_approvals_load_error'
}

export const getApprovalsHomeRoute = pathname => {
  if (pathname?.startsWith('/mvu-lite') || pathname?.startsWith('/mvu-essentials')) return '/mvu-lite'
  if (pathname?.startsWith('/mvu-accelerator')) return '/mvu-accelerator'
  return '/dashboard'
}

export default function useController() {
  const { t } = useI18n()
  const history = useHistory()
  const location = useLocation()
  const requestIdRef = useRef(0)
  const [summary, setSummary] = useState(null)
  const [canViewApprovals, setCanViewApprovals] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [rowAction, setRowAction] = useState({})

  const loadSummary = useCallback(async ({ silent = false } = {}) => {
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    if (!silent) setLoading(true)

    const result = await ReadMyPendingAccess({
      page,
      pageSize: PAGE_SIZE,
      search: debouncedSearch,
    })
    if (requestId !== requestIdRef.current) return

    if (!result?.ok) {
      setSummary(null)
      if (result?.status === 401 || result?.status === 403) {
        setCanViewApprovals(false)
        if (result.status === 403) {
          history.replace(getApprovalsHomeRoute(location.pathname))
        }
      } else {
        setCanViewApprovals(true)
        setError(t(getApprovalErrorKey(result?.status)))
      }
    } else {
      setCanViewApprovals(true)
      setSummary(result?.data || null)
      setError('')
      const responsePage = Number(result?.data?.pagination?.page)
      if (Number.isInteger(responsePage) && responsePage !== page) {
        setPage(responsePage)
      }
    }
    setLoading(false)
    setLoaded(true)
  }, [debouncedSearch, history, location.pathname, page, t])

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setPage(1)
      setDebouncedSearch(search.trim())
    }, 300)
    return () => window.clearTimeout(timeout)
  }, [search])

  useEffect(() => {
    loadSummary()
  }, [loadSummary])

  const handleDecision = useCallback(async (userId, action, options = {}) => {
    setRowAction(previous => ({ ...previous, [userId]: action }))
    const result = action === 'approve'
      ? await ApproveMyPendingAccess(userId, options.companyId, options.useRegistrationCompany, options)
      : await RejectMyPendingAccess(userId)

    if (!result?.ok && result?.data?.code === 'salesforce_review_required') {
      setRowAction(previous => { const next = { ...previous }; delete next[userId]; return next })
      return { reviewRequired: true }
    }
    if (!result?.ok) {
      if (result?.status === 409) {
        toast.info(t('user_approvals_already_processed'))
        await loadSummary({ silent: true })
      } else if (!exposeStrapiError(result?.data)) {
        toast.error(t('user_approvals_process_error'))
      }
    } else {
      toast.success(t(action === 'approve'
        ? 'user_approvals_approve_success'
        : 'user_approvals_reject_success'))
      await loadSummary({ silent: true })
    }

    setRowAction(previous => {
      const next = { ...previous }
      delete next[userId]
      return next
    })
    return !!result?.ok
  }, [loadSummary, t])

  return {
    summary,
    canViewApprovals,
    loading,
    loaded,
    error,
    search,
    rowAction,
    t,
    retry: loadSummary,
    onSearchChange: event => setSearch(event.target.value),
    onPageChange: (event, nextPage) => setPage(nextPage + 1),
    onDecision: handleDecision,
    loadCompanyOptions: ReadMyPendingAccessCompanies,
    loadIdentityReview: ReadMyPendingIdentityReview,
  }
}
